import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
  afterNextRender,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';

import type { EsnIconButtonSize } from '../../../shared/types/atoms';
import type { EsnActionsMenuSize, EsnMenuItem, EsnMenuPlacement } from '../../../shared/types/data';
import { ButtonComponent } from '../../atoms/button/button.component';
import { IconButtonComponent } from '../../atoms/icon-button/icon-button.component';
import { IconComponent } from '../../atoms/icon/icon.component';

const DEFAULT_WIDTH: Record<EsnActionsMenuSize, number> = { sm: 112, md: 160 };
const OFFSET = 4;
const VIEWPORT_MARGIN = 8;

/**
 * A row's or card's overflow menu: a dots trigger that opens a short list of
 * actions, with the destructive one set apart below a hairline as a filled
 * StarOrange button so it cannot be hit by accident.
 */
@Component({
  selector: 'esn-actions-menu',
  imports: [IconComponent, IconButtonComponent, ButtonComponent],
  templateUrl: './actions-menu.component.html',
  styleUrl: './actions-menu.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:click)': 'handleDocumentClick($event)',
    '(window:resize)': 'close(false)',
    '(window:scroll)': 'position()',
  },
})
export class ActionsMenuComponent<Id extends string = string> {
  readonly items = input.required<readonly EsnMenuItem<Id>[]>();
  readonly size = input<EsnActionsMenuSize>('md');

  /**
   * Menu width in px; defaults to the Figma width of the size (112 / 160).
   */
  readonly width = input<number | null>(null);

  readonly placement = input<EsnMenuPlacement>('bottom-end');
  readonly triggerSize = input<EsnIconButtonSize>(32);

  /**
   * Accessible name of the dots trigger.
   */
  readonly label = input('More actions');

  /**
   * When set, the trigger is a labelled secondary button with a chevron (the detail header's
   * "Actions ⌄") instead of the dots glyph; `label` stays its accessible name only for the dots.
   */
  readonly triggerLabel = input<string | null>(null);
  readonly disabled = input(false);

  readonly select = output<EsnMenuItem<Id>>();
  readonly openChange = output<boolean>();

  protected readonly open = signal(false);
  protected readonly coords = signal<{ top: number; left: number }>({ top: 0, left: 0 });

  protected readonly regularItems = computed(() => this.items().filter((item) => !item.danger));
  protected readonly dangerItems = computed(() => this.items().filter((item) => item.danger));
  protected readonly menuWidth = computed(() => this.width() ?? DEFAULT_WIDTH[this.size()]);

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly injector = inject(Injector);

  /**
   * Opens the menu and moves focus to its first available action.
   */
  show(): void {
    if (this.open() || this.disabled()) return;
    this.open.set(true);
    this.openChange.emit(true);
    afterNextRender(
      () => {
        const menu = this.menu();
        if (!menu) return;
        if (typeof menu.showPopover === 'function' && !menu.matches(':popover-open')) menu.showPopover();
        this.position();
        this.focusItem(0, 1);
      },
      { injector: this.injector },
    );
  }

  /**
   * Closes the menu; by default focus goes back to the trigger so keyboard users keep their place.
   */
  close(restoreFocus = true): void {
    if (!this.open()) return;
    const menu = this.menu();
    if (menu && typeof menu.hidePopover === 'function' && menu.matches(':popover-open')) menu.hidePopover();
    this.open.set(false);
    this.openChange.emit(false);
    if (restoreFocus) this.trigger()?.focus();
  }

  protected toggle(): void {
    if (this.open()) this.close();
    else this.show();
  }

  protected choose(item: EsnMenuItem<Id>): void {
    if (item.disabled) return;
    this.select.emit(item);
    this.close();
  }

  protected handleKeydown(event: KeyboardEvent): void {
    const items = this.menuItems();
    const current = items.indexOf(document.activeElement as HTMLButtonElement);
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.focusItem(current + 1, 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusItem(current < 0 ? items.length - 1 : current - 1, -1);
        break;
      case 'Home':
        event.preventDefault();
        this.focusItem(0, 1);
        break;
      case 'End':
        event.preventDefault();
        this.focusItem(items.length - 1, -1);
        break;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        this.close();
        break;
      case 'Tab':
        this.close(false);
        break;
    }
  }

  protected handleDocumentClick(event: MouseEvent): void {
    if (this.open() && !this.host.nativeElement.contains(event.target as Node)) this.close(false);
  }

  /**
   * Places the menu against the trigger, flipping above it when the viewport runs out below.
   */
  protected position(): void {
    const trigger = this.trigger();
    const menu = this.menu();
    if (!this.open() || !trigger || !menu) return;

    const rect = trigger.getBoundingClientRect();
    const height = menu.offsetHeight;
    const width = this.menuWidth();
    const [vertical, horizontal] = this.placement().split('-') as ['top' | 'bottom', 'start' | 'end'];

    const below = rect.bottom + OFFSET;
    const above = rect.top - OFFSET - height;
    const fitsBelow = below + height <= window.innerHeight - VIEWPORT_MARGIN;
    const fitsAbove = above >= VIEWPORT_MARGIN;
    const top = vertical === 'bottom' ? (fitsBelow || !fitsAbove ? below : above) : fitsAbove || !fitsBelow ? above : below;

    const rawLeft = horizontal === 'end' ? rect.right - width : rect.left;
    const left = Math.max(VIEWPORT_MARGIN, Math.min(rawLeft, window.innerWidth - width - VIEWPORT_MARGIN));
    this.coords.set({ top, left });
  }

  private focusItem(start: number, step: 1 | -1): void {
    const items = this.menuItems();
    if (items.length === 0) return;
    for (let n = 0; n < items.length; n++) {
      const index = (((start + n * step) % items.length) + items.length) % items.length;
      if (!items[index].disabled) {
        items[index].focus();
        return;
      }
    }
  }

  private menu(): HTMLElement | null {
    return this.host.nativeElement.querySelector<HTMLElement>('[role="menu"]');
  }

  private trigger(): HTMLButtonElement | null {
    return this.host.nativeElement.querySelector<HTMLButtonElement>('esn-icon-button button, esn-button button');
  }

  private menuItems(): HTMLButtonElement[] {
    return Array.from(this.host.nativeElement.querySelectorAll<HTMLButtonElement>('[role="menuitem"]'));
  }
}
